import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse, PaginatedResponse } from '@/types';

// ===============================================
// API CLIENT CONFIGURATION
// ===============================================

class ApiClient {
  private client: AxiosInstance;
  private dataClient: AxiosInstance;
  private baseURL: string;
  private dataBaseURL: string;

  constructor() {
    this.baseURL = import.meta.env.VITE_API_URL || '/api';
    // Data stack endpoints - will be configured via environment or fallback to main stack
    this.dataBaseURL = import.meta.env.VITE_DATA_API_URL || this.baseURL;
    
    // Main stack client
    this.client = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Data stack client
    this.dataClient = axios.create({
      baseURL: this.dataBaseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Setup interceptors for both clients
    const setupClientInterceptors = (client: AxiosInstance) => {
      // Request interceptor to add auth token
      client.interceptors.request.use(
        (config) => {
          // Try to get token from auth store first, fallback to localStorage
          let token = null;
          
          try {
            // Get token from Zustand persisted storage
            const authStorage = localStorage.getItem('auth-storage');
            if (authStorage) {
              const parsedAuth = JSON.parse(authStorage);
              token = parsedAuth.state?.token;
            }
          } catch (error) {
            console.warn('Failed to parse auth storage:', error);
          }
          
          // Fallback to direct localStorage lookup
          if (!token) {
            token = localStorage.getItem('auth_token');
          }
          
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          
          return config;
        },
        (error) => Promise.reject(error)
      );
    };

    setupClientInterceptors(this.client);
    setupClientInterceptors(this.dataClient);

    // Response interceptor for error handling (apply to both clients)
    const setupResponseInterceptors = (client: AxiosInstance) => {
      client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.warn('🔐 [AUTH] Token expired or invalid - logging out user');
          
          // Clear all authentication data
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth-storage');
          
          // Try to import and use auth store for clean logout
          import('@/store/authStore').then(({ useAuthStore }) => {
            const store = useAuthStore.getState();
            store.logout();
          }).catch(() => {
            console.warn('Could not access auth store for logout');
          });
          
          // Redirect to login page
          const currentPath = window.location.pathname;
          if (currentPath !== '/login' && currentPath !== '/') {
            console.log('🔄 [AUTH] Redirecting to login page...');
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
    };

    setupResponseInterceptors(this.client);
    setupResponseInterceptors(this.dataClient);
  }

  // Determine which client to use based on the endpoint
  private getClientForEndpoint(url: string): AxiosInstance {
    // Data stack endpoints
    const dataStackEndpoints = [
      '/data-collection',
      '/data-collector',
      '/market/'  // All market endpoints go to data stack
    ];

    const isDataStackEndpoint = dataStackEndpoints.some(endpoint => url.startsWith(endpoint));
    return isDataStackEndpoint ? this.dataClient : this.client;
  }

  // Generic request methods with smart routing
  async get<T>(url: string, params?: any): Promise<ApiResponse<T>> {
    const client = this.getClientForEndpoint(url);
    const response: AxiosResponse<ApiResponse<T>> = await client.get(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const client = this.getClientForEndpoint(url);
    const response: AxiosResponse<ApiResponse<T>> = await client.post(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const client = this.getClientForEndpoint(url);
    const response: AxiosResponse<ApiResponse<T>> = await client.put(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const client = this.getClientForEndpoint(url);
    const response: AxiosResponse<ApiResponse<T>> = await client.patch(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    const client = this.getClientForEndpoint(url);
    const response: AxiosResponse<ApiResponse<T>> = await client.delete(url);
    return response.data;
  }

  async getPaginated<T>(url: string, params?: any): Promise<PaginatedResponse<T>> {
    const client = this.getClientForEndpoint(url);
    const response: AxiosResponse<PaginatedResponse<T>> = await client.get(url, { params });
    return response.data;
  }
}

// Create singleton instance
export const apiClient = new ApiClient();



// ===============================================
// TRADING PAIRS API
// ===============================================

export const tradingPairsApi = {
  getAll: (params?: { page?: number; limit?: number; includeDetails?: boolean }) =>
    apiClient.getPaginated<any>('/trading/pairs', params),

  getById: (id: string) =>
    apiClient.get<any>(`/trading/pairs/${id}`),

  create: (data: any) =>
    apiClient.post<any>('/trading/pairs', data),

  update: (id: string, data: any) =>
    apiClient.put<any>(`/trading/pairs/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/trading/pairs/${id}`),

  getPortfolioSummary: () =>
    apiClient.get<any>('/trading/pairs/portfolio/summary'),
};

// ===============================================
// STRATEGIES API
// ===============================================

export const strategiesApi = {
  getAll: (params?: { page?: number; limit?: number }) =>
    apiClient.getPaginated<any>(usePublicEndpoints ? '/public/strategies' : '/strategies', params),

  getById: (id: string) =>
    apiClient.get<any>(`/strategies/${id}`),

  create: (data: any) =>
    apiClient.post<any>('/strategies', data),

  update: (id: string, data: any) =>
    apiClient.put<any>(`/strategies/${id}`, data),

  delete: (id: string) =>
    apiClient.delete(`/strategies/${id}`),

  execute: (id: string) =>
    apiClient.post<any>(`/strategies/${id}/execute`),

  clone: (id: string, name: string) =>
    apiClient.post<any>(`/strategies/${id}/clone`, { name }),

  getPerformance: (id: string, params?: { period?: string }) =>
    apiClient.get<any>(`/strategies/${id}/performance`, params),
};

// ===============================================
// TRADES API
// ===============================================

export const tradesApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    strategyId?: string;
    tradingPairId?: string;
    tradeType?: string;
    status?: string;
    isPaperTrade?: boolean;
    dateFrom?: string;
    dateTo?: string;
    includeDetails?: boolean;
  }) =>
    apiClient.getPaginated<any>('/trades', params),

  getById: (id: string) =>
    apiClient.get<any>(`/trades/${id}`),

  getStatistics: (params?: {
    strategyId?: string;
    dateFrom?: string;
    dateTo?: string;
    isPaperTrade?: boolean;
  }) =>
    apiClient.get<any>('/trades/statistics', params),

  getProfitLoss: (params?: {
    strategyId?: string;
    dateFrom?: string;
    dateTo?: string;
    isPaperTrade?: boolean;
  }) =>
    apiClient.get<any>('/trades/profit-loss', params),
};

// ===============================================
// PAPER TRADING API
// ===============================================

// Use authenticated endpoints by default, public only for explicit testing
const usePublicEndpoints = import.meta.env.VITE_USE_PUBLIC_ENDPOINTS === 'true';

export const paperTradingApi = {
  getTrades: (params?: { page?: number; limit?: number }) =>
    apiClient.getPaginated<any>(usePublicEndpoints ? '/public/paper-trading/trades' : '/paper-trading/trades', params),

  createTrade: (data: any) =>
    apiClient.post<any>('/paper-trading/trades', data),

  deleteTrade: (id: string) =>
    apiClient.delete(`/paper-trading/trades/${id}`),

  getPerformance: (params?: { strategyId?: string; dateFrom?: string; dateTo?: string }) =>
    apiClient.get<any>(usePublicEndpoints ? '/public/paper-trading/performance' : '/paper-trading/performance', params),

  getPortfolio: () =>
    apiClient.get<any>(usePublicEndpoints ? '/public/paper-trading/portfolio' : '/paper-trading/portfolio'),

  executeStrategy: (strategyId?: string) =>
    apiClient.post<any>('/paper-trading/execute' + (strategyId ? `/${strategyId}` : '')),

  resetPortfolio: (initialBalance?: number) =>
    apiClient.post<any>('/paper-trading/portfolio/reset', { initialBalance }),
};

// ===============================================
// LIVE TRADING API
// ===============================================

export const liveTradingApi = {
  getTrades: (params?: { page?: number; limit?: number; includeDetails?: boolean }) =>
    apiClient.getPaginated<any>('/live-trading/trades', params),

  getPerformance: (params?: { strategyId?: string; dateFrom?: string; dateTo?: string; period?: string }) =>
    apiClient.get<any>('/live-trading/performance', params),

  executeStrategy: (strategyId?: string) =>
    apiClient.post<any>('/live-trading/execute' + (strategyId ? `/${strategyId}` : '')),

  getActiveOrders: (exchange: string) =>
    apiClient.get<any>('/live-trading/orders/active', { exchange }),

  cancelOrder: (data: { exchange: string; orderId: string; symbol: string }) =>
    apiClient.post<any>('/live-trading/orders/cancel', data),

  emergencyStop: () =>
    apiClient.post<any>('/live-trading/emergency-stop'),

  getProfitLoss: (params?: { strategyId?: string; dateFrom?: string; dateTo?: string }) =>
    apiClient.get<any>('/live-trading/profit-loss', params),

  getStatistics: (params?: { strategyId?: string; dateFrom?: string; dateTo?: string }) =>
    apiClient.get<any>('/live-trading/statistics', params),
};

// ===============================================
// EXCHANGE API
// ===============================================

export const exchangeApi = {
  // API Keys Management
  getApiKeys: (params?: { page?: number; limit?: number }) =>
    apiClient.getPaginated<any>('/exchange/apikeys', params),

  createApiKey: (data: any) =>
    apiClient.post<any>('/exchange/apikeys', data),

  updateApiKey: (id: string, data: any) =>
    apiClient.put<any>(`/exchange/apikeys/${id}`, data),

  deleteApiKey: (id: string) =>
    apiClient.delete(`/exchange/apikeys/${id}`),

  testApiKey: (id: string) =>
    apiClient.post<any>(`/exchange/apikeys/${id}/test`),

  // Multi-Exchange Capabilities & Features
  getCapabilities: (params?: { exchange?: string; feature?: string }) =>
    apiClient.get<any>('/exchange/capabilities', params),

  getAggregatedBalance: (params?: { exchanges?: string; includeZero?: boolean; currency?: string }) =>
    apiClient.get<any>('/exchange/aggregated-balance', params),

  getTradingPairs: (params?: { exchanges?: string; baseAsset?: string; quoteAsset?: string; active?: boolean }) =>
    apiClient.get<any>('/exchange/trading-pairs', params),

  getHealthCheck: () =>
    apiClient.get<any>('/exchange/health-check'),

  // Market Data
  getBalance: (exchange?: string) =>
    apiClient.get<any>('/exchange/balance', { exchange }),

  getMarkets: (exchange?: string) =>
    apiClient.get<any>('/exchange/markets', { exchange }),

  getTicker: (params?: { symbol?: string; exchange?: string }) =>
    apiClient.get<any>('/exchange/ticker', params),

  getOHLCV: (params?: { symbol?: string; timeframe?: string; limit?: number; exchange?: string }) =>
    apiClient.get<any>('/exchange/ohlcv', params),

  // Trading
  placeOrder: (data: any) =>
    apiClient.post<any>('/exchange/orders', data),

  getOrder: (orderId: string, params?: { exchange?: string }) =>
    apiClient.get<any>(`/exchange/orders/${orderId}`, params),

  cancelOrder: (orderId: string, params?: { exchange?: string; symbol?: string }) =>
    apiClient.delete(`/exchange/orders/${orderId}${params ? `?${new URLSearchParams(params as any).toString()}` : ''}`),

  getOrderHistory: (params?: { symbol?: string; limit?: number; exchange?: string }) =>
    apiClient.get<any>('/exchange/orders', params),

  testConnection: (data: { exchange: string }) =>
    apiClient.post<any>('/exchange/test', data),
};



// ===============================================
// BACKTESTING API
// ===============================================

export const backtestingApi = {
  // Get user's backtest history
  getBacktests: (params?: { 
    limit?: number; 
    offset?: number; 
    strategyId?: string; 
    symbol?: string; 
    sortBy?: string; 
    sortOrder?: string; 
  }) =>
    apiClient.get<any>('/backtests', params),

  // Create and execute a new backtest
  createBacktest: (data: {
    strategyId: string;
    symbol: string;
    startDate: string;
    endDate: string;
    initialBalance: number;
    config?: any;
  }) =>
    apiClient.post<any>('/backtests', data),

  // Get detailed backtest results by ID
  getBacktestById: (backtestId: string) =>
    apiClient.get<any>(`/backtests/${backtestId}`),

  // Delete a backtest
  deleteBacktest: (backtestId: string) =>
    apiClient.delete(`/backtests/${backtestId}`),

  // Compare multiple backtests
  compareBacktests: (backtestIds: string[]) =>
    apiClient.get<any>('/backtests/compare', { backtestIds: backtestIds.join(',') }),
};

// ===============================================
// AUTOMATED TRADING API
// ===============================================

export const automationApi = {
  // Get user's automated trading sessions
  getSessions: (params?: { 
    limit?: number; 
    offset?: number; 
    status?: string; 
    isActive?: boolean;
    sortBy?: string; 
    sortOrder?: string; 
  }) =>
    apiClient.get<any>('/automation', params),
    
  // Create a new automation session
  createSession: (data: {
    name: string;
    description?: string;
    isPaperTrading?: boolean;
    executionInterval?: number;
    maxDailyExecutions?: number;
    strategies: string[];
    strategyExecutionMode?: string;
    maxDailyVolume?: number;
    maxPositionSizePct?: number;
    stopLossPct?: number;
  }) =>
    apiClient.post<any>('/automation', data),
    
  // Get automation session by ID
  getSessionById: (id: string, includeExecutions?: boolean) =>
    apiClient.get<any>(`/automation/${id}`, includeExecutions ? { includeExecutions: 'true' } : undefined),
    
  // Start an automation session
  startSession: (id: string) =>
    apiClient.post<any>(`/automation/${id}/start`),
    
  // Stop an automation session
  stopSession: (id: string) =>
    apiClient.post<any>(`/automation/${id}/stop`),
    
  // Manually execute an automation session
  executeSession: (id: string) =>
    apiClient.post<any>(`/automation/${id}/execute`),
    
  // Delete an automation session
  deleteSession: (id: string) =>
    apiClient.delete(`/automation/${id}`),
    
  // Get execution history for a session
  getExecutions: (sessionId: string, params?: { limit?: number; offset?: number; status?: string }) =>
    apiClient.get<any>(`/automation/${sessionId}/executions`, params),
    
  // Get automation health status
  getHealthStatus: () =>
    apiClient.get<any>('/automation/health')
};

// ===============================================
// AI ASSISTANT API
// ===============================================

export const aiApi = {
  chat: (data: { message: string; includeContext?: boolean }) =>
    apiClient.post<any>('/ai/chat', data),

  analyze: (data: { strategyId?: string; type?: 'portfolio' | 'strategy' }) =>
    apiClient.post<any>('/ai/analyze', data),

  getSentiment: (data?: { symbols?: string[] }) =>
    apiClient.post<any>('/ai/sentiment', data),
};

// ===============================================
// MONITORING API
// ===============================================

export const monitoringApi = {
  getDashboardMetrics: (params?: { timeRange?: string; granularity?: string }) =>
    apiClient.get<any>('/monitoring/dashboard', params),

  getSystemHealth: () =>
    apiClient.get<any>('/monitoring/health'),

  getComponentMetrics: (component: string) =>
    apiClient.get<any>(`/monitoring/components/${component}`),
};

// ===============================================
// HEALTH CHECK API
// ===============================================

// ===============================================
// AUTH API
// ===============================================

export const authApi = {
  login: (data: { email: string; password: string }) =>
    apiClient.post<any>('/auth/login', data),

  register: (data: { email: string; password: string; firstName?: string; lastName?: string }) =>
    apiClient.post<any>('/auth/register', data),
};

// ===============================================
// USER PROFILE API
// ===============================================

export const userApi = {
  getProfile: () =>
    apiClient.get<any>('/user/profile'),

  updateSettings: (settings: any) =>
    apiClient.put<any>('/user/settings', settings),
};

// ===============================================
// HEALTH CHECK API
// ===============================================

export const healthApi = {
  check: () =>
    apiClient.get<any>('/health'),
};

// ===============================================
// RISK MANAGEMENT API
// ===============================================

export const riskApi = {
  // Get user's risk profile
  getRiskProfile: (profileId?: string) =>
    apiClient.get<any>('/risk/profile', profileId ? { profileId } : {}),

  // Update risk profile
  updateRiskProfile: (profileData: any) =>
    apiClient.post<any>('/risk/profile', profileData),

  // Get risk monitoring status
  getMonitoring: () =>
    apiClient.get<any>('/risk/monitoring'),

  // Analyze portfolio risk
  analyzePortfolio: (portfolioData: any) =>
    apiClient.post<any>('/risk/analyze', portfolioData),

  // Get active emergency stops
  getEmergencyStops: () =>
    apiClient.get<any>('/risk/emergency-stop'),

  // Trigger emergency stop
  triggerEmergencyStop: (stopData: { reason: string; scope?: string; affectedStrategies?: string[]; affectedPairs?: string[]; affectedAutomationSessions?: string[] }) =>
    apiClient.post<any>('/risk/emergency-stop', stopData),

  // Lift emergency stop
  liftEmergencyStop: (emergencyStopId: string, reason: string) =>
    apiClient.post<any>(`/risk/emergency-stop/${emergencyStopId}/lift`, { reason }),

  // Get risk events
  getRiskEvents: (params?: { limit?: number; offset?: number; severity?: string; eventType?: string; resolved?: boolean; sortOrder?: string }) =>
    apiClient.get<any>('/risk/events', params),

  // Risk management health check
  getHealthCheck: () =>
    apiClient.get<any>('/risk/health')
};

// Advanced Analytics API
export const analyticsApi = {
  // Get comprehensive portfolio performance analytics
  getPerformanceAnalytics: (params?: {
    period?: number;
    compareToMarket?: boolean;
    includeAttribution?: boolean;
    tradingMode?: 'paper' | 'live';
  }) =>
    apiClient.get<any>('/analytics/performance', params),
  
  // Get advanced risk metrics and analysis
  getRiskMetrics: (params?: {
    period?: number;
    confidenceLevel?: number;
    includeCorrelations?: boolean;
    tradingMode?: 'paper' | 'live';
  }) =>
    apiClient.get<any>('/analytics/risk-metrics', params),
  
  // Get performance attribution analysis
  getAttributionAnalysis: (params?: {
    period?: number;
    attributionType?: string;
    tradingMode?: 'paper' | 'live';
  }) =>
    apiClient.get<any>('/analytics/attribution', params),
  
  // Get benchmark comparison analysis
  getBenchmarkComparisons: (params?: {
    period?: number;
    benchmarks?: string[];
    tradingMode?: 'paper' | 'live';
  }) =>
    apiClient.get<any>('/analytics/benchmarks', params),
  
  // Get enhanced portfolio overview with analytics
  getPortfolioOverview: (params?: {
    includePositions?: boolean;
    includeAllocations?: boolean;
    includeMetrics?: boolean;
    tradingMode?: 'paper' | 'live';
  }) =>
    apiClient.get<any>('/analytics/portfolio', params),
  
  // Analytics service health check
  getHealthCheck: () =>
    apiClient.get<any>('/analytics/health')
}; 